import { Stack, Typography } from '@mui/material';
import { motion } from 'motion/react';
import { FC, useMemo } from 'react';
import { VIEW_SECTION_TYPE } from '../../../../data-models/view-config.data-model';
import { ViewModel } from '../../../../models/view.model';
import { useView } from '../../hooks/useView';
import { ChartsSection } from '../ChartsSection/ChartsSection';
import { KPISection } from '../KPISection/KPISection';
import { AppliedFiltersIndicator } from '../OverviewTable/Table/AppliedFiltersIndicator';
import { ViewTable } from '../OverviewTable/Table/ViewTable';

type SectionComponent = FC<{ id: string; view: ViewModel }>;

export const ViewSections: FC<{ view: ViewModel }> = ({ view }) => {
  const { getSectionsOrder, getSection } = useView();

  const sectionComponents = useMemo<Record<VIEW_SECTION_TYPE, SectionComponent>>(
    () => ({
      [VIEW_SECTION_TYPE.KPI]: KPISection,
      [VIEW_SECTION_TYPE.CHART]: ChartsSection,
      [VIEW_SECTION_TYPE.TABLE]: ViewTable,
    }),
    []
  );
  const sectionOrder = getSectionsOrder();

  return (
    <motion.div animate={{ opacity: [0, 1] }} transition={{ duration: 1 }} key={view.id}>
      <ViewTitle view={view} />
      {sectionOrder
        .map((sectionId) => {
          const type = getSection(sectionId).type;
          const Component = sectionComponents[type];

          return !Component ? null : <Component id={sectionId} view={view} key={`${view.id}-${sectionId}`} />;
        })
        .filter((c) => !!c)}
    </motion.div>
  );
};

export function ViewTitle({ view }: { view: ViewModel }) {
  const { viewId, name } = view;
  let title: string;
  let desc = ''; // FIXME: the description will need to be added to view model
  switch (viewId) {
    case 'overview': {
      title = name || 'Overview';
      desc = '';
      break;
    }
    case 'return_forecast': {
      title = name || 'Return Forecast';
      desc = '';
      break;
    }
    case 'round_tracker': {
      title = name || 'Round Tracker';
      desc = '';
      break;
    }
    default: {
      title = name;
    }
  }

  return (
    <Stack
      p='1rem 2rem 1rem'
      gap={'0.5rem'}
      position={'sticky'}
      top={0}
      zIndex={1}
      sx={{ backdropFilter: 'blur(20px)', background: 'rgba(255, 255, 255, 0.4)' }}
      display={'grid'}
      gridTemplateColumns={'1fr auto'}
    >
      <Stack direction={'row'} alignItems={'center'} gap={'1rem'}>
        <Typography variant='h4'>{title}</Typography>
        {Boolean(desc) && (
          <Typography variant='body2' color='text.secondary' sx={{ whiteSpace: 'pre-line' }}>
            {desc}
          </Typography>
        )}
        <AppliedFiltersIndicator view={view} />
      </Stack>
    </Stack>
  );
}
